import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Loader2, Shirt, Plus, BarChart3, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { LinenDialog } from "@/components/LinenDialog";
import { LINEN_ITEMS } from "@/lib/linenItems";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

import { tx, lz, uiLocale } from "@/i18n";
const fmtDT = (s) => { try { return new Date(s).toLocaleString(uiLocale(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
const summary = (items) => LINEN_ITEMS.filter(({ key }) => items?.[key] > 0).map(({ key, label }) => `${label}: ${items[key]}`);
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Linen() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const isHost = user.role === "host";
  const [houses, setHouses] = useState([]);
  const [houseId, setHouseId] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [history, setHistory] = useState(null);
  const [report, setReport] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editReg, setEditReg] = useState(null);
  const [delReg, setDelReg] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { api.get("/houses").then(({ data }) => setHouses(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const params = {};
    if (houseId !== "all") params.house_id = houseId;
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/linen", { params }).then(({ data }) => setHistory(data)).catch((e) => toast.error(apiError(e)));
    if (isAdmin) api.get("/linen/report", { params }).then(({ data }) => setReport(data)).catch(() => {});
  }, [houseId, start, end, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const houseName = (id) => houses.find((h) => h.id === id)?.name || tx("Woning");
  const activeHouse = houseId !== "all" ? houseId : null;
  const [search, setSearch] = useState("");

  const visibleHistory = (history || []).filter((r) => {
    if (!isHost || !search.trim()) return true;
    const q = search.toLowerCase();
    const hay = [r.house_name || houseName(r.house_id), r.created_by_name, r.anders, r.note, ...summary(r.items)]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  const canEdit = (r) => isAdmin || (isHost && (r.created_at || "").slice(0, 10) === todayISO());

  const doDelete = async () => {
    if (!delReg) return;
    setDeleting(true);
    try {
      await api.delete(`/linen/${delReg.id}`);
      toast.success(tx("Registratie verwijderd"));
      setDelReg(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="linen-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Shirt className="h-6 w-6 text-secondary" /> {tx("Linnen")}</h1>
          <p className="text-muted-foreground mt-1">{tx("Registratiehistorie van wasstukken per woning.")}</p>
        </div>
        <Button onClick={() => setDlgOpen(true)} className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="linen-new-btn">
          <Plus className="h-4 w-4" /> {tx("Nieuwe registratie")}
        </Button>
      </div>

      {isHost ? (
        <div className="relative" data-testid="linen-host-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tx("Zoek op woning, item of notitie…")} className="pl-9" data-testid="linen-search-input" />
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border p-4 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="linen-filters">
          <div>
            <label className="text-xs font-medium">{tx("Woning")}</label>
            <Select value={houseId} onValueChange={setHouseId}>
              <SelectTrigger className="mt-1" data-testid="linen-house-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tx("Alle woningen")}</SelectItem>
                {houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-medium">{tx("Vanaf")}</label><DatePicker value={start} onChange={setStart} placeholder={tx("Begin")} className="mt-1" testId="linen-start-input" /></div>
          <div><label className="text-xs font-medium">{tx("Tot")}</label><DatePicker value={end} onChange={setEnd} placeholder={tx("Eind")} className="mt-1" testId="linen-end-input" /></div>
        </div>
      )}

      {isAdmin && (
        <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="linen-report">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-1"><BarChart3 className="h-5 w-5 text-primary" /> {tx("Totalen per item")}</h2>
          <p className="text-xs text-muted-foreground mb-3">{report ? tx("{0} registratie(s) in selectie", { 0: report.registrations }) : "…"}</p>
          {report && (
            <div className="flex items-center justify-between gap-3 mb-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2" data-testid="linen-grand-total">
              <span className="text-sm font-medium">{tx("Totaal van alle was-items")}</span>
              <span className="text-2xl font-bold text-primary">{report.grand_total || 0}</span>
            </div>
          )}
          {report && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
              {LINEN_ITEMS.map(({ key, label }) => (
                <div key={key} className={`rounded-lg border p-2 text-center ${report.totals[key] > 0 ? "border-secondary/40 bg-secondary/5" : "border-border"}`} data-testid={`linen-total-${key}`}>
                  <div className="text-[11px] text-muted-foreground truncate" title={label}>{label}</div>
                  <div className="text-lg font-bold">{report.totals[key] || 0}</div>
                </div>
              ))}
              <div className={`rounded-lg border p-2 text-center ${report.anders_total > 0 ? "border-amber-400/50 bg-amber-100/40" : "border-border"}`} data-testid="linen-total-anders">
                <div className="text-[11px] text-muted-foreground truncate" title={tx("Anders")}>{tx("Anders")}</div>
                <div className="text-lg font-bold">{report.anders_total || 0}</div>
              </div>
            </div>
          )}
          {report?.anders_notes?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="linen-anders-notes"><span className="font-medium">{tx("Anders:")}</span> {report.anders_notes.join(" · ")}</p>
          )}
        </div>
      )}

      <div className="space-y-3">
        <h2 className="font-display font-semibold text-lg">{tx("Historie")}</h2>
        {!history ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : visibleHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="linen-empty">{tx("Nog geen registraties in deze selectie.")}</p>
        ) : (
          visibleHistory.map((r) => {
            const s = summary(r.items);
            return (
              <div key={r.id} className="bg-card rounded-xl border border-border p-4 shadow-sm" data-testid={`linen-row-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium">{r.house_name || houseName(r.house_id)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{fmtDT(r.created_at)} · {r.created_by_name || "—"}{r.updated_at ? tx(" · bewerkt") : ""}</span>
                    {canEdit(r) && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditReg(r)} data-testid={`linen-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDelReg(r)} data-testid={`linen-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {s.length ? s.map((t, i) => <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full">{t}</span>) : <span className="text-xs text-muted-foreground">{tx("Geen items")}</span>}
                  {r.anders && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{tx("Anders:")} {r.anders}{r.anders_aantal ? `: ${r.anders_aantal}` : ""}</span>}
                </div>
                {r.note && <p className="text-xs text-muted-foreground mt-1 italic">"{r.note}"</p>}
              </div>
            );
          })
        )}
      </div>

      <LinenDialog open={dlgOpen} onOpenChange={setDlgOpen} houseId={activeHouse} houseName={activeHouse ? houseName(activeHouse) : ""} houses={houses} onSaved={load} />
      <LinenDialog open={!!editReg} onOpenChange={(o) => { if (!o) setEditReg(null); }} registration={editReg} onSaved={load} />

      <AlertDialog open={!!delReg} onOpenChange={(o) => { if (!o) setDelReg(null); }}>
        <AlertDialogContent data-testid="linen-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{tx("Registratie verwijderen?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {tx("Deze linnen-registratie wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="linen-delete-cancel">{tx("Annuleren")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className="bg-destructive hover:bg-destructive/90" data-testid="linen-delete-confirm">
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Verwijderen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
